/**
 * Fractional ordering.
 *
 * Cards and columns are ordered by a `REAL` column rather than by an integer
 * index. Moving an item writes exactly one row: the moved item's new position,
 * computed as the midpoint between its new neighbours. Nothing else in the
 * column changes, so a drag does not contend with every other card in the
 * column and does not produce an O(n) burst of UPDATEs that the Durable Object
 * would have to serialise.
 *
 * The cost of that choice is the midpoint problem. Repeatedly inserting at the
 * head of a column halves the distance to the first element: starting from a
 * gap of 1024, the 24th successive head insert produces a gap below
 * `REBALANCE_EPSILON` (1e-4). Once that happens, float64 can no longer represent
 * a distinct value between the neighbours and the ordering silently collapses
 * (two cards compare equal, and their order becomes whatever SQLite returns).
 * `planPosition` therefore *detects* the condition and returns a rebalance
 * instruction instead of a position. The caller renumbers the whole column with
 * fresh 1024-spaced values. Renumbering is O(n) writes, but it happens on the
 * order of once per 24 head inserts rather than on every move.
 *
 * Everything here is pure and total. No I/O, no clock, no randomness - which is
 * what makes the edge cases testable without a runtime.
 */

import { POSITION_GAP, REBALANCE_EPSILON } from './domain.js';

export { POSITION_GAP, REBALANCE_EPSILON };

/**
 * The outcome of asking "where should this item go?".
 *
 * `assign` means the caller can write the returned position directly.
 * `rebalance` means the caller must renumber the column first and then retry;
 * it carries a machine-readable `reason` so the DO can log *why* a column had
 * to be renumbered rather than just that it did.
 */
export type PositionPlan =
  | { readonly kind: 'assign'; readonly position: number }
  | { readonly kind: 'rebalance'; readonly reason: RebalanceReason };

export type RebalanceReason =
  /** Two neighbours are closer together than the smallest usable gap. */
  | 'gap_too_small'
  /** Head insert would produce a position indistinguishable from zero. */
  | 'head_gap_exhausted'
  /** Neighbours arrived out of order, which means the column's stored data is
   * already inconsistent (a concurrent write that bypassed the DO, or a
   * migration bug). Renumbering is the only safe recovery. */
  | 'neighbours_out_of_order'
  /** A neighbour was NaN or infinite. Refusing to compute is better than
   * propagating a NaN into the ordering column, where it would compare false
   * against everything and make the card unfindable by any ORDER BY. */
  | 'non_finite_neighbour';

/** A position is only usable if it is a finite number strictly greater than
 * zero. Zero and negatives are rejected because `planPosition` uses the head
 * position as a divisor, and a zero would produce Infinity. */
function isUsable(position: number | null): position is number {
  return position !== null && Number.isFinite(position) && position > 0;
}

/**
 * Compute the position for an item being inserted between two neighbours.
 *
 * @param before Position of the item that will sit immediately above, or `null`
 *               when the item lands at the head of the column.
 * @param after  Position of the item that will sit immediately below, or `null`
 *               when the item lands at the tail.
 *
 * The three interesting cases:
 *
 * - **Empty column** (`null`, `null`) - start at `POSITION_GAP`. Starting at 1
 *   would be the obvious choice and is wrong: it leaves only one representable
 *   value below the first item, so the very first head insert would have
 *   nowhere to go. Starting at 1024 leaves ten halvings of headroom.
 *
 * - **Head** (`null`, after) - halve the distance to the first item. The floor
 *   is checked against `REBALANCE_EPSILON` *before* returning, not after, so a
 *   caller can never receive a position it would be unable to subdivide later.
 *
 * - **Tail** (before, `null`) - add a full `POSITION_GAP`. Appending is by far
 *   the most common operation (new cards, drags to the end of a column) and it
 *   never degrades: the position grows linearly and float64 represents integers
 *   exactly up to 2^53, so a tail insert cannot trigger a rebalance.
 */
export function planPosition(before: number | null, after: number | null): PositionPlan {
  const hasBefore = before !== null;
  const hasAfter = after !== null;

  if (hasBefore && !Number.isFinite(before)) return { kind: 'rebalance', reason: 'non_finite_neighbour' };
  if (hasAfter && !Number.isFinite(after)) return { kind: 'rebalance', reason: 'non_finite_neighbour' };

  // Empty column. The only case where neither neighbour constrains us.
  if (!hasBefore && !hasAfter) return { kind: 'assign', position: POSITION_GAP };

  // Head insert: subdivide (0, after).
  if (!hasBefore) {
    // `hasAfter` is true here; the earlier guards make this a narrowing, not a
    // runtime assumption.
    const afterValue = after as number;
    if (!isUsable(afterValue)) return { kind: 'rebalance', reason: 'head_gap_exhausted' };
    const candidate = afterValue / 2;
    if (candidate < REBALANCE_EPSILON) return { kind: 'rebalance', reason: 'head_gap_exhausted' };
    return { kind: 'assign', position: candidate };
  }

  // Tail insert: extend past the last item. Cannot exhaust.
  if (!hasAfter) {
    return { kind: 'assign', position: (before as number) + POSITION_GAP };
  }

  const beforeValue = before as number;
  const afterValue = after as number;

  // Guard against corrupted ordering data before doing arithmetic on it.
  if (!isUsable(beforeValue) || !isUsable(afterValue)) {
    return { kind: 'rebalance', reason: 'non_finite_neighbour' };
  }
  if (afterValue <= beforeValue) {
    return { kind: 'rebalance', reason: 'neighbours_out_of_order' };
  }

  const gap = afterValue - beforeValue;
  if (gap <= REBALANCE_EPSILON) return { kind: 'rebalance', reason: 'gap_too_small' };

  return { kind: 'assign', position: beforeValue + gap / 2 };
}

/**
 * The result of a move expressed against a column's current ordering.
 * Distinct from `PositionPlan` because a move has one extra failure mode that an
 * insert does not: the caller can name a destination that no longer exists.
 */
export type MovePlan =
  | { readonly kind: 'assign'; readonly position: number }
  | { readonly kind: 'rebalance'; readonly reason: RebalanceReason }
  /** The moving item is already exactly where it was asked to go. Writing the
   * row would bump `version` and broadcast a no-op event, which shows up in
   * every peer's activity feed as a card move that did not happen. */
  | { readonly kind: 'noop' };

/**
 * Plan a move given the positions of the *other* items in the destination
 * column, sorted ascending, and the index the moving item should occupy after
 * insertion.
 *
 * `targetIndex` is clamped rather than rejected. A drag that ends one pixel
 * below the last card, or a keyboard reorder that lands at `length + 1`, is a
 * legitimate user intent that the client cannot always express precisely, and
 * returning a 400 for it would be pedantic. Clamping to `[0, neighbours.length]`
 * means "head" and "tail" absorb all off-by-one cases.
 *
 * `currentPosition` is the moving item's stored position, or `null` when it is
 * arriving from another column (or is new). It exists solely to detect a
 * same-column no-op: the destination neighbours already straddle the item, so
 * writing anything would bump `version` and broadcast a move that did not
 * happen. Callers that pass `null` simply lose that optimisation, which is the
 * safe direction to fail in.
 */
export function planMove(
  sortedNeighbourPositions: readonly number[],
  targetIndex: number,
  currentPosition: number | null = null,
): MovePlan {
  const clamped = Math.max(0, Math.min(Math.trunc(targetIndex), sortedNeighbourPositions.length));

  // Neighbours straddling the insertion point. `?? null` rather than an
  // assertion because `noUncheckedIndexedAccess` is on and the index is
  // provably in range only after the clamp above.
  const before = clamped > 0 ? (sortedNeighbourPositions[clamped - 1] ?? null) : null;
  const after = clamped < sortedNeighbourPositions.length ? (sortedNeighbourPositions[clamped] ?? null) : null;

  if (currentPosition !== null && Number.isFinite(currentPosition)) {
    const above = before === null || currentPosition > before;
    const below = after === null || currentPosition < after;
    if (above && below) return { kind: 'noop' };
  }

  const plan = planPosition(before, after);
  return plan.kind === 'assign' ? plan : { kind: 'rebalance', reason: plan.reason };
}

/**
 * Renumber a column's items with fresh, evenly spaced positions.
 *
 * The input order is the intended final order; the output maps each id to its
 * new position. Spacing is a full `POSITION_GAP` so that after a rebalance the
 * column has the same headroom it had when it was first created - the rebalance
 * restores the invariant rather than merely postponing the next one.
 *
 * Positions are `(index + 1) * POSITION_GAP`, i.e. they start at 1024 and never
 * touch zero, preserving the "position > 0" invariant that `planPosition`
 * depends on for head inserts.
 */
export function rebalance(orderedIds: readonly string[]): Map<string, number> {
  const result = new Map<string, number>();
  orderedIds.forEach((id, index) => {
    result.set(id, (index + 1) * POSITION_GAP);
  });
  return result;
}

/** Sort a list of positioned items ascending, returning a new array. Ties are
 * broken by id so the result is deterministic; without a tiebreak, SQLite's
 * ordering for equal positions is unspecified and a rebalance triggered by a
 * tie could itself reorder two visually identical cards differently on each
 * peer, producing a board that disagrees with itself. */
export function sortByPosition<T extends { position: number; id: string }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => (a.position === b.position ? a.id.localeCompare(b.id) : a.position - b.position));
}

/** The position a brand-new item should take to land at the end of a column,
 * given that column's current positions. Returns `POSITION_GAP` for an empty
 * column so the first item and a rebalanced first item agree. */
export function appendPosition(existingPositions: readonly number[]): number {
  if (existingPositions.length === 0) return POSITION_GAP;
  return Math.max(...existingPositions) + POSITION_GAP;
}

// ---------------------------------------------------------------------------
// Full placement planning
// ---------------------------------------------------------------------------

export interface PlacementItem {
  id: string;
  position: number;
}

/**
 * The complete outcome of a reorder, including the case where the column has to
 * be renumbered.
 *
 * `rebalance` carries the new position for *every* item in the column, including
 * the one being moved, because a rebalance is a renumbering of the whole set. The
 * caller writes them all in one batch. Returning only the moved item's position
 * in that case would be wrong: the assignments were computed against a fresh
 * 1024-spaced layout, so any item left with its old position would collide.
 */
export type PlacementPlan =
  | { kind: 'noop' }
  | { kind: 'assign'; position: number }
  | { kind: 'rebalance'; assignments: Map<string, number>; position: number };

/**
 * Plan a move end to end.
 *
 * `existing` is every *other* item in the destination column, in any order.
 * `movingId` is the item being placed, which must not appear in `existing`.
 *
 * The rebalance branch is computed by first materialising the intended final
 * order (the existing items with the moving item spliced in at the target
 * index) and then renumbering that order. That ordering of the two steps is what
 * makes the result correct: renumbering first and then looking for a midpoint
 * would compute the midpoint against positions that no longer exist.
 */
export function planPlacement(
  existing: readonly PlacementItem[],
  movingId: string,
  targetIndex: number,
  currentPosition: number | null = null,
): PlacementPlan {
  const ordered = sortByPosition(existing);
  const clamped = Math.max(0, Math.min(Math.trunc(targetIndex), ordered.length));

  const plan = planMove(
    ordered.map((item) => item.position),
    clamped,
    currentPosition,
  );

  if (plan.kind === 'noop') return { kind: 'noop' };

  if (plan.kind === 'assign') return { kind: 'assign', position: plan.position };

  // Rebalance: splice the moving item into the intended final order, then
  // renumber the whole thing.
  const finalOrder = ordered.map((item) => item.id);
  finalOrder.splice(clamped, 0, movingId);

  const assignments = rebalance(finalOrder);
  return { kind: 'rebalance', assignments, position: assignments.get(movingId) ?? POSITION_GAP };
}
